import { apiClient } from './axios';

export interface MessagingConversation {
  id: string;
  contact_id: string | null;
  configuration_id: string | null;
  assigned_user_id: string | null;
  channel: 'WHATSAPP' | 'SMS' | 'EMAIL' | 'WEB_WIDGET';
  channel_address: string;
  remote_address: string;
  status: 'OPEN' | 'CLOSED' | 'ARCHIVED';
  last_message_at: string | null;
  last_message_preview: string | null;
  unread_count: number;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  contact: { id: string; name: string; phone: string } | null;
  configuration: { id: string; name: string } | null;
  assigned_user: { id: string; name: string; email: string } | null;
}

export interface MessagingMessage {
  id: string;
  conversation_id: string;
  direction: 'INBOUND' | 'OUTBOUND';
  status: 'PENDING' | 'SENT' | 'DELIVERED' | 'READ' | 'FAILED';
  type: 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'TEMPLATE' | 'INTERACTIVE' | 'AUDIO' | 'LOCATION';
  content: string | null;
  media_url: string | null;
  channel_message_id: string | null;
  sender_id: string | null;
  metadata: Record<string, unknown> | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  reply_to_message_id?: string | null;
  reply_to_content?: string | null;
  reply_to_direction?: 'INBOUND' | 'OUTBOUND' | null;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

export interface StartConversationDto {
  phoneNumber: string;
  configurationId: string;
  channel: 'WHATSAPP' | 'SMS' | 'EMAIL' | 'WEB_WIDGET';
  templateName?: string;
  templateLanguage?: string;
  message?: string;
  components?: Array<Record<string, unknown>>;
}

export interface ConversationQuery {
  channel?: string;
  configurationId?: string;
  status?: string;
  search?: string;
  assignedUserId?: string;
  unassigned?: boolean;
  page?: number;
  limit?: number;
}

export const messagingApi = {
  getConversations: async (query: ConversationQuery = {}) => {
    const response = await apiClient.get<PaginatedResponse<MessagingConversation>>(
      '/messaging/conversations',
      { params: query },
    );
    return response.data;
  },

  getConversation: async (id: string) => {
    const response = await apiClient.get<MessagingConversation>(`/messaging/conversations/${id}`);
    return response.data;
  },

  getMessages: async (conversationId: string, query: { page?: number; limit?: number } = {}) => {
    const response = await apiClient.get<PaginatedResponse<MessagingMessage>>(
      `/messaging/conversations/${conversationId}/messages`,
      { params: query },
    );
    return response.data;
  },

  sendMessage: async (conversationId: string, content: string) => {
    const response = await apiClient.post<MessagingMessage>(
      `/messaging/conversations/${conversationId}/messages`,
      { content },
    );
    return response.data;
  },

  markAsRead: async (conversationId: string) => {
    await apiClient.patch(`/messaging/conversations/${conversationId}/read`);
  },

  updateStatus: async (conversationId: string, status: 'OPEN' | 'CLOSED' | 'ARCHIVED') => {
    const response = await apiClient.patch<MessagingConversation>(
      `/messaging/conversations/${conversationId}/status`,
      { status },
    );
    return response.data;
  },

  sendTemplate: async (
    conversationId: string,
    templateName: string,
    language: string = 'en_US',
    components?: Array<Record<string, unknown>>,
    renderedText?: string,
  ) => {
    const response = await apiClient.post<MessagingMessage>(
      `/messaging/conversations/${conversationId}/template`,
      { templateName, language, components, renderedText },
    );
    return response.data;
  },

  deleteConversation: async (conversationId: string) => {
    await apiClient.delete(`/messaging/conversations/${conversationId}`);
  },

  startConversation: async (dto: StartConversationDto) => {
    const response = await apiClient.post<MessagingConversation>(
      '/messaging/conversations/start',
      dto,
    );
    return response.data;
  },

  takeoverConversation: async (conversationId: string) => {
    const response = await apiClient.post<MessagingConversation>(
      `/messaging/conversations/${conversationId}/takeover`,
    );
    return response.data;
  },

  handbackConversation: async (conversationId: string) => {
    const response = await apiClient.post<MessagingConversation>(
      `/messaging/conversations/${conversationId}/handback`,
    );
    return response.data;
  },

  setAutoHandback: async (conversationId: string, minutes: number | null) => {
    const response = await apiClient.patch<MessagingConversation>(
      `/messaging/conversations/${conversationId}/auto-handback`,
      { minutes },
    );
    return response.data;
  },

  claimConversation: async (conversationId: string) => {
    const response = await apiClient.post<MessagingConversation>(
      `/messaging/conversations/${conversationId}/claim`,
    );
    return response.data;
  },

  unclaimConversation: async (conversationId: string) => {
    const response = await apiClient.post<MessagingConversation>(
      `/messaging/conversations/${conversationId}/unclaim`,
    );
    return response.data;
  },

  sendImage: async (conversationId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<MessagingMessage>(
      `/messaging/conversations/${conversationId}/media`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  getMediaSignedUrl: async (path: string) => {
    const response = await apiClient.get<{ url: string }>('/messaging/media/signed-url', {
      params: { path },
    });
    return response.data.url;
  },
};
